import React from 'react';

const PrivacyPolicy = () => {
    return (
        <div className="privacy-page">
            <div className="privacy-container">
                <div className="privacy-content">
                    <div className="privacy-header">
                        <h1>Privacy Policy for meDZhub</h1>
                        <p className="last-updated">Effective Date: November 24, 2025 | Last Updated: November 24, 2025</p>
                    </div>

                    <div className="privacy-intro">
                        <h2>Introduction</h2>
                        <p>
                            Dhruthzuci Technology Solutions Private Limited ("we," "our," or "us") operates the meDZhub mobile application and web platform (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                        </p>
                        <p>
                            We are committed to protecting your privacy and ensuring the security of your personal information. Please read this Privacy Policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy.
                        </p>
                        <p className="privacy-warning">
                            If you do not agree with the terms of this Privacy Policy, please do not access or use the Service.
                        </p>
                    </div>

                    <div className="privacy-section">
                        <h2>1. Information We Collect</h2>
                        
                        <h3>1.1 Personal Information</h3>
                        <p>We collect personal information that you voluntarily provide to us when you register for and use the Service:</p>
                        
                        <h4>For All Users:</h4>
                        <ul>
                            <li>Full name</li>
                            <li>Email address</li>
                            <li>Phone number</li>
                            <li>Username and password</li>
                            <li>User role (Rehab Admin, Staff/Therapist and Patients)</li>
                        </ul>

                        <h4>For Patients:</h4>
                        <ul>
                            <li>Diagnosis and medical history</li>
                            <li>Emergency contact information</li>
                            <li>Home address and location coordinates</li>
                            <li>Date of birth</li>
                            <li>Gender</li>
                            <li>Health conditions and rehabilitation needs</li>
                            <li>Therapy session records and progress notes</li>
                            <li>Assessment and re-assessment documents</li>
                            <li>Consent forms and standard operating procedure acknowledgments</li>
                        </ul>

                        <h4>For Staff/Therapists:</h4>
                        <ul>
                            <li>Professional specialization</li>
                            <li>Years of experience</li>
                            <li>Professional ratings and performance metrics</li>
                            <li>Work availability and schedule</li>
                            <li>Assigned rehabilitation center</li>
                        </ul>

                        <h4>For Rehab Center Administrators:</h4>
                        <ul>
                            <li>Rehabilitation center details</li>
                            <li>Center location and contact information</li>
                            <li>Business registration information</li>
                        </ul>

                        <h3>1.2 Location Information</h3>
                        <p>We collect precise location data when you use the Service for the following purposes:</p>
                        <ul>
                            <li><strong>Session Tracking:</strong> When staff members start a therapy session, we collect GPS coordinates to record trip start location, arrival at patient location, and session completion location</li>
                            <li><strong>Travel Reports:</strong> We calculate distance traveled and travel time for staff members between locations</li>
                            <li><strong>Service Delivery:</strong> We use patient addresses to facilitate home care service delivery</li>
                            <li><strong>Geographic Analytics:</strong> We analyze service areas and coverage for rehabilitation centers</li>
                        </ul>
                        <p>Location data collection occurs only when:</p>
                        <ul>
                            <li>Staff members actively start a therapy session (status changes to "en_route")</li>
                            <li>Staff members update session status (arrived, in_service, completed)</li>
                            <li>During active therapy sessions</li>
                        </ul>
                        <p>You can manage location permissions through your device settings, but disabling location services may limit the functionality of the Service.</p>

                        <h3>1.3 Health Information</h3>
                        <p>As a healthcare service platform, we collect and process sensitive health information, including:</p>
                        <ul>
                            <li>Medical diagnoses and conditions</li>
                            <li>Therapy session records and progress notes</li>
                            <li>Assessment and re-assessment documents</li>
                            <li>Treatment plans and rehabilitation packages</li>
                            <li>Session completion records</li>
                            <li>Health-related photographs (with consent)</li>
                            <li>Provider notes and observations</li>
                        </ul>
                        <p>We handle all health information in compliance with applicable healthcare privacy laws and regulations.</p>

                        <h3>1.4 Documents and Media</h3>
                        <p>We collect documents and media files that you upload to the Service:</p>
                        <ul>
                            <li>Consent forms (required for first session)</li>
                            <li>Standard Operating Procedure (SOP) acknowledgments</li>
                            <li>Initial assessment documents</li>
                            <li>Session progress documents</li>
                            <li>Re-assessment documents (every 8th session)</li>
                            <li>Photographs for progress tracking</li>
                            <li>Session reports and notes</li>
                        </ul>

                        <h3>1.5 Usage and Device Information</h3>
                        <p>We automatically collect certain information when you access and use the Service:</p>
                        <ul>
                            <li>Device type and model</li>
                            <li>Operating system and version</li>
                            <li>IP address</li>
                            <li>App version</li>
                            <li>Usage patterns and preferences</li>
                            <li>Session timestamps and duration</li>
                            <li>Crash reports and error logs</li>
                            <li>Device permissions (camera, storage, location)</li>
                        </ul>

                        <h3>1.6 Appointment and Session Data</h3>
                        <p>We collect information related to service delivery:</p>
                        <ul>
                            <li>Appointment scheduling information</li>
                            <li>Session start and end times</li>
                            <li>Session status updates (scheduled, confirmed, en_route, arrived, in_service, completed, cancelled)</li>
                            <li>Service types and duration</li>
                            <li>Package details (number of sessions, cost, completion status)</li>
                            <li>Staff assignments</li>
                            <li>Cancellation reasons</li>
                        </ul>
                    </div>

                    <div className="privacy-section">
                        <h2>2. How We Use Your Information</h2>
                        <p>We use the collected information for the following purposes:</p>

                        <h3>2.1 Service Delivery</h3>
                        <ul>
                            <li>Facilitate rehabilitation and home care services</li>
                            <li>Schedule and manage therapy appointments</li>
                            <li>Enable staff-patient matching and assignment</li>
                            <li>Track therapy session progress</li>
                            <li>Coordinate care between rehabilitation centers, staff, and patients</li>
                        </ul>

                        <h3>2.2 Communication</h3>
                        <ul>
                            <li>Send appointment reminders and notifications</li>
                            <li>Send SMS alerts for incomplete sessions at end of day</li>
                            <li>Provide service updates and important announcements</li>
                            <li>Respond to user inquiries and support requests</li>
                            <li>Send administrative communications</li>
                        </ul>

                        <h3>2.3 Business Operations</h3>
                        <ul>
                            <li>Process payments and manage billing</li>
                            <li>Generate reports and analytics for rehabilitation centers</li>
                            <li>Monitor staff performance and service quality</li>
                            <li>Calculate travel distance and time for reimbursement</li>
                            <li>Manage service availability and scheduling</li>
                        </ul>

                        <h3>2.4 Improvement and Development</h3>
                        <ul>
                            <li>Improve and optimize the Service</li>
                            <li>Develop new features and functionality</li>
                            <li>Analyze usage patterns and trends</li>
                            <li>Conduct research to enhance service delivery</li>
                            <li>Debug and fix technical issues</li>
                        </ul>

                        <h3>2.5 Legal Compliance</h3>
                        <ul>
                            <li>Comply with applicable laws and regulations</li>
                            <li>Respond to legal requests and prevent fraud</li>
                            <li>Protect the rights, property, and safety of users</li>
                            <li>Enforce our terms of service and policies</li>
                        </ul>

                        <h3>2.6 Quality Assurance</h3>
                        <ul>
                            <li>Monitor service quality and patient outcomes</li>
                            <li>Ensure compliance with standard operating procedures</li>
                            <li>Track completion of required documentation</li>
                            <li>Evaluate staff performance and service delivery</li>
                        </ul>
                    </div>

                    <div className="privacy-section">
                        <h2>3. How We Share Your Information</h2>
                        <p>We may share your information in the following circumstances:</p>

                        <h3>3.1 With Service Providers</h3>
                        <p>We share information with authorized users within the platform:</p>
                        <ul>
                            <li>Patients can view their assigned staff, appointments, and session records</li>
                            <li>Staff/Therapists can view information about their assigned patients, packages, and appointments</li>
                            <li>Rehab Admins can view all information related to their rehabilitation center, including staff, patients, services, and appointments</li>
                        </ul>

                        <h3>3.2 With Third-Party Service Providers</h3>
                        <p>We may share information with third-party service providers who perform services on our behalf:</p>
                        <ul>
                            <li>Cloud storage and hosting providers</li>
                            <li>Analytics and performance monitoring services</li>
                            <li>SMS notification services</li>
                            <li>Payment processing services</li>
                            <li>Technical support and maintenance providers</li>
                        </ul>
                        <p>These service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>

                        <h3>3.3 For Legal Reasons</h3>
                        <p>We may disclose your information when required by law or in response to:</p>
                        <ul>
                            <li>Valid legal processes (court orders, subpoenas, warrants)</li>
                            <li>Government or regulatory requests</li>
                            <li>Legal investigations</li>
                            <li>Protection of our rights, property, or safety</li>
                            <li>Protection of users' rights, property, or safety</li>
                            <li>Prevention of fraud or illegal activities</li>
                        </ul>

                        <h3>3.4 Business Transfers</h3>
                        <p>In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change and provide choices regarding your information.</p>

                        <h3>3.5 With Your Consent</h3>
                        <p>We may share your information for other purposes with your explicit consent.</p>
                    </div>

                    <div className="privacy-section">
                        <h2>4. Data Security</h2>
                        <p>We implement appropriate technical and organizational security measures to protect your information:</p>

                        <h3>4.1 Security Measures</h3>
                        <ul>
                            <li>Encryption of data in transit and at rest</li>
                            <li>Secure authentication and access controls</li>
                            <li>Regular security assessments and updates</li>
                            <li>Role-based access restrictions</li>
                            <li>Secure cloud infrastructure</li>
                            <li>Regular backups and disaster recovery procedures</li>
                        </ul>

                        <h3>4.2 Staff Security Practices</h3>
                        <ul>
                            <li>Staff members must securely logout when finished using the Service</li>
                            <li>Device security requirements for staff members</li>
                            <li>Prohibition on sharing login credentials</li>
                            <li>Mandatory security training for staff and administrators</li>
                        </ul>

                        <h3>4.3 Limitations</h3>
                        <p>While we strive to protect your information, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your information.</p>
                    </div>

                    <div className="privacy-section">
                        <h2>5. Data Retention</h2>
                        <p>We retain your information for as long as necessary to fulfill the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law.</p>

                        <h3>5.1 Retention Periods</h3>
                        <ul>
                            <li><strong>Account Information:</strong> Retained while your account is active and for a reasonable period thereafter</li>
                            <li><strong>Health Records:</strong> Retained in accordance with applicable healthcare regulations and legal requirements (typically 7-10 years)</li>
                            <li><strong>Session Records:</strong> Retained for quality assurance and legal compliance purposes</li>
                            <li><strong>Location Data:</strong> Retained only for the duration necessary for travel reporting and billing purposes</li>
                            <li><strong>Documents:</strong> Retained as part of patient health records</li>
                        </ul>

                        <h3>5.2 Deletion Requests</h3>
                        <p>You may request deletion of your information by contacting us. We will respond to your request within a reasonable timeframe, subject to legal and regulatory retention requirements.</p>
                    </div>

                    <div className="privacy-section">
                        <h2>6. Your Rights and Choices</h2>
                        <p>Depending on your location, you may have the following rights:</p>

                        <h3>6.1 Access and Portability</h3>
                        <ul>
                            <li>Request access to your personal information</li>
                            <li>Request a copy of your information in a portable format</li>
                            <li>View your session history and appointment records</li>
                        </ul>

                        <h3>6.2 Correction</h3>
                        <ul>
                            <li>Update or correct your personal information</li>
                            <li>Edit your profile information through the app</li>
                            <li>Request correction of inaccurate health records</li>
                        </ul>

                        <h3>6.3 Deletion</h3>
                        <ul>
                            <li>Request deletion of your account and associated information</li>
                            <li>Subject to legal and regulatory retention requirements</li>
                            <li>Note that deletion may affect your ability to receive services</li>
                        </ul>

                        <h3>6.4 Objection and Restriction</h3>
                        <ul>
                            <li>Object to certain uses of your information</li>
                            <li>Request restriction of processing in certain circumstances</li>
                            <li>Withdraw consent for optional data collection</li>
                        </ul>

                        <h3>6.5 Location Services</h3>
                        <ul>
                            <li>Enable or disable location services through device settings</li>
                            <li>Note that disabling location may limit service functionality</li>
                        </ul>

                        <h3>6.6 Notifications</h3>
                        <ul>
                            <li>Manage notification preferences</li>
                            <li>Opt-out of non-essential communications</li>
                            <li>Critical service notifications may still be sent</li>
                        </ul>

                        <p>To exercise these rights, please contact us using the information provided in Section 13.</p>
                    </div>

                    <div className="privacy-section">
                        <h2>7. Children's Privacy</h2>
                        <p>
                            The Service is not intended for use by children under the age of 13 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children under 13.
                        </p>
                        <p>
                            If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. If we become aware that we have collected personal information from children without parental consent, we will take steps to delete that information.
                        </p>
                    </div>

                    <div className="privacy-section">
                        <h2>8. International Data Transfers</h2>
                        <p>
                            Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from the laws of your country.
                        </p>
                        <p>We ensure that appropriate safeguards are in place to protect your information in accordance with this Privacy Policy and applicable laws, including:</p>
                        <ul>
                            <li>Standard contractual clauses</li>
                            <li>Adequacy decisions by regulatory authorities</li>
                            <li>Other legally approved transfer mechanisms</li>
                        </ul>
                        <p>By using the Service, you consent to the transfer of your information to countries outside your country of residence.</p>
                    </div>

                    <div className="privacy-section">
                        <h2>9. Third-Party Links and Services</h2>
                        <p>
                            The Service may contain links to third-party websites, applications, or services that are not operated by us. We are not responsible for the privacy practices of these third parties. We encourage you to review the privacy policies of any third-party services before providing them with your information.
                        </p>
                    </div>

                    <div className="privacy-section">
                        <h2>10. Changes to This Privacy Policy</h2>
                        <p>
                            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
                        </p>
                        <ul>
                            <li>Posting the updated Privacy Policy in the app</li>
                            <li>Updating the "Last Updated" date at the top of this policy</li>
                            <li>Sending you an email or in-app notification (for significant changes)</li>
                        </ul>
                        <p>
                            Your continued use of the Service after the effective date of the revised Privacy Policy constitutes your acceptance of the changes. We encourage you to review this Privacy Policy periodically.
                        </p>
                    </div>

                    <div className="privacy-section">
                        <h2>11. Healthcare Privacy Compliance</h2>
                        <p>
                            As a healthcare service platform, we comply with applicable healthcare privacy regulations, including but not limited to:
                        </p>
                        <ul>
                            <li>Health Insurance Portability and Accountability Act (HIPAA) where applicable</li>
                            <li>Personal Data Protection laws in India</li>
                            <li>Other applicable regional healthcare privacy regulations</li>
                        </ul>
                        <p>We implement administrative, physical, and technical safeguards to protect health information in accordance with these regulations.</p>
                    </div>

                    <div className="privacy-section">
                        <h2>12. Specific Data Practices</h2>

                        <h3>12.1 Automatic Session Reminders</h3>
                        <p>
                            If a therapy session is not properly closed (left in "scheduled" or "in_service" status), both the staff member and admin will receive an SMS reminder at the end of the day. This ensures proper documentation and billing.
                        </p>

                        <h3>12.2 Document Upload Requirements</h3>
                        <ul>
                            <li><strong>First Session:</strong> Consent form, SOP acknowledgment, and initial assessment required</li>
                            <li><strong>Ongoing Sessions:</strong> Progress document required at session completion</li>
                            <li><strong>Every 8th Session:</strong> Re-assessment document required</li>
                        </ul>
                        <p>These requirements ensure quality care and regulatory compliance.</p>

                        <h3>12.3 Performance Tracking</h3>
                        <p>
                            We track staff performance metrics including completion rates, on-time arrivals, and patient outcomes. This information is used to maintain service quality and is visible to rehabilitation center administrators.
                        </p>

                        <h3>12.4 Travel and Distance Tracking</h3>
                        <p>We calculate and store travel distance and time for staff members for the purposes of:</p>
                        <ul>
                            <li>Reimbursement and billing</li>
                            <li>Service area analysis</li>
                            <li>Operational efficiency</li>
                            <li>Daily and monthly travel reports</li>
                        </ul>
                    </div>

                    <div className="privacy-section">
                        <h2>13. Contact Us</h2>
                        <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
                        <div className="privacy-contact">
                            <p><strong>Dhruthzuci Technology Solutions Private Limited</strong></p>
                            <p>Email: <a href="mailto:medzhub@eshatechgroup.com">medzhub@eshatechgroup.com</a></p>
                            <p>Phone: <a href="tel:+918031196612">+91 8031196612</a></p>
                            {/* <p>Support Email: <a href="mailto:support@medzhub.com">support@medzhub.com</a></p> */}
                            <p>For data protection inquiries, please mark your communication as "Privacy Request" to ensure prompt handling.</p>
                        </div>
                    </div>

                    <div className="privacy-section">
                        <h2>14. Supervisory Authority</h2>
                        <p>
                            If you are located in the European Economic Area (EEA), United Kingdom, or other jurisdictions with data protection authorities, you have the right to lodge a complaint with your local supervisory authority if you believe our processing of your personal information violates applicable law.
                        </p>
                    </div>

                    <div className="privacy-section">
                        <h2>15. Consent</h2>
                        <p>By using the meDZhub Service, you consent to:</p>
                        <ul>
                            <li>The collection and use of your information as described in this Privacy Policy</li>
                            <li>The transfer of your information to our servers and service providers</li>
                            <li>The use of location services for session tracking and travel reporting</li>
                            <li>The processing of health information for service delivery and quality assurance</li>
                        </ul>
                        <p>You may withdraw your consent at any time by discontinuing use of the Service and requesting account deletion, subject to legal retention requirements.</p>
                    </div>

                    <div className="privacy-footer-note">
                        <p>
                            <strong>— — —</strong>
                        </p>
                        <p>
                            This Privacy Policy is governed by the laws of India and is designed to comply with Apple App Store requirements, HIPAA guidelines, and applicable data protection regulations.
                        </p>
                        <p>
                            For the most current version of this Privacy Policy, please check the app or visit our website.
                        </p>
                        <p>
                            © 2025 Dhruthzuci Technology Solutions Private Limited. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;

